import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import SubscriptionTable from "./SubscriptionTable";
import SubscriptionForm from "./SubscriptionForm";
import ReferralRewardForm from "./ReferralRewardForm";
import ReferralRewardTable from "./ReferralRewardTable";
import AddonTable from "./AddonTable";
import AddonForm from "./AddonForm";
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import { AuthContext } from "../../AuthContext/AuthContext";
import { BASE_URL } from "../../Api/ApiUrls";
import { RAZORPAY_URL } from "../../Api/ApiUrls";

const AdminSubscription = () => {
  const [plans, setPlans] = useState([]);
  const [addOnPlans, setAddOnPlans] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const { userId, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState({
    plans: true,
    addOnPlans: true,
    rewards: true,
  });
  const [modalOpen, setModalOpen] = useState({
    plans: false,
    addOnPlans: false,
    rewards: false,
  });
  const [currentItem, setCurrentItem] = useState({
    plan: null,
    addOnPlan: null,
    reward: null,
  });
  const [refreshFlag, setRefreshFlag] = useState({
    plans: false,
    addOnPlans: false,
    rewards: false,
  });

  // Fetch plans from API
  const fetchPlans = async () => {
    try {
      setLoading((prev) => ({ ...prev, plans: true }));
      const response = await fetch(`${BASE_URL}/subplans/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setPlans(result);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching subscription plans:", err);
    } finally {
      setLoading((prev) => ({ ...prev, plans: false }));
    }
  };

  // Fetch add-on plans from API
  const fetchAddOnPlans = async () => {
    try {
      setLoading((prev) => ({ ...prev, addOnPlans: true }));
      const response = await fetch(`${BASE_URL}/add-on/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setAddOnPlans(result);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching add-on plans:", err);
    } finally {
      setLoading((prev) => ({ ...prev, addOnPlans: false }));
    }
  };

  // Fetch rewards from API
  const fetchRewards = async () => {
    try {
      setLoading((prev) => ({ ...prev, rewards: true }));
      const response = await fetch(`${BASE_URL}/rewards/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setRewards(result);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching referral rewards:", err);
    } finally {
      setLoading((prev) => ({ ...prev, rewards: false }));
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchAddOnPlans();
    fetchRewards();
  }, [refreshFlag.plans, refreshFlag.addOnPlans, refreshFlag.rewards]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Plans handlers
  const handleAddPlanClick = () => {
    setCurrentItem((prev) => ({ ...prev, plan: null }));
    setModalOpen((prev) => ({ ...prev, plans: true }));
  };

  const handleEditPlanClick = (item) => {
    setCurrentItem((prev) => ({ ...prev, plan: item }));
    setModalOpen((prev) => ({ ...prev, plans: true }));
  };

  const handleDeletePlanClick = async (item) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the "${item.plan_name}" plan?`,
    );

    if (!isConfirmed) {
      return; // Exit if user cancels
    }

    try {
      const response = await fetch(`${BASE_URL}/subplans/${item.plan_id}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRefreshFlag((prev) => ({ ...prev, plans: !prev.plans }));
      alert("Plan deleted successfully!"); // Optional success notification
    } catch (err) {
      console.error("Error deleting plan:", err);
      setError(err.message);
      alert("Failed to delete plan. Please try again."); // Error notification
    }
  };

  // AddOn Plans handlers
  const handleAddAddOnPlanClick = () => {
    setCurrentItem((prev) => ({ ...prev, addOnPlan: null }));
    setModalOpen((prev) => ({ ...prev, addOnPlans: true }));
  };

  const handleEditAddOnPlanClick = (item) => {
    setCurrentItem((prev) => ({ ...prev, addOnPlan: item }));
    setModalOpen((prev) => ({ ...prev, addOnPlans: true }));
  };

  const handleDeleteAddOnPlanClick = async (item) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the "${item.user_type}" addonplan?`,
    );

    if (!isConfirmed) {
      return; // Exit if user cancels
    }

    try {
      const response = await fetch(`${BASE_URL}/add-on/${item.addOn_id}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRefreshFlag((prev) => ({ ...prev, addOnPlans: !prev.addOnPlans }));
      alert("AddPlan deleted successfully!");
    } catch (err) {
      console.error("Error deleting add-on plan:", err);
      setError(err.message);
      alert("Failed to delete addplan. Please try again.");
    }
  };

  // Rewards handlers
  const handleAddRewardClick = () => {
    setCurrentItem((prev) => ({ ...prev, reward: null }));
    setModalOpen((prev) => ({ ...prev, rewards: true }));
  };

  const handleEditRewardClick = (item) => {
    setCurrentItem((prev) => ({ ...prev, reward: item }));
    setModalOpen((prev) => ({ ...prev, rewards: true }));
  };

  const handleDeleteRewardClick = async (item) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the reward?`,
    );

    if (!isConfirmed) {
      return; // Exit if user cancels
    }

    try {
      const response = await fetch(`${BASE_URL}/rewards/${item.rew_id}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRefreshFlag((prev) => ({ ...prev, rewards: !prev.rewards }));
      alert("Reward deleted successfully!");
    } catch (err) {
      console.error("Error deleting reward:", err);
      setError(err.message);
      alert("Failed to delete reward. Please try again.");
    }
  };

  // const handlePlanSubmit = async (formData) => {
  //   try {
  //     // Fetch all existing plans first
  //     const existingResponse = await fetch(`${BASE_URL}/subplans/`);
  //     const existingPlans = await existingResponse.json();

  //     // Check if a plan with same plan_name and user_type exists (case-insensitive)
  //     const isDuplicate = existingPlans.some(plan =>
  //       plan.plan_name.toLowerCase() === formData.plan_name.toLowerCase() &&
  //       plan.user_type.toLowerCase() === formData.user_type.toLowerCase() &&
  //       (!currentItem.plan || plan.plan_id !== currentItem.plan.plan_id) // Allow update to the same item
  //     );

  //     if (isDuplicate) {
  //       alert(`A plan with name "${formData.plan_name}" already exists for user type "${formData.user_type}".`);
  //       return; // Stop submission
  //     }

  //     const url = currentItem.plan
  //       ? `${BASE_URL}/subplans/${currentItem.plan.plan_id}/`
  //       : `${BASE_URL}/subplans/`;
  //     const method = currentItem.plan ? 'PUT' : 'POST';

  //     const response = await fetch(url, {
  //       method,
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(formData)
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     setModalOpen(prev => ({ ...prev, plans: false }));
  //     setRefreshFlag(prev => ({ ...prev, plans: !prev.plans }));
  //   } catch (err) {
  //     console.error("Error submitting plan:", err);
  //     setError(err.message);
  //   }
  // };

  const handlePlanSubmit = async (formData) => {
    try {
      // Fetch all existing plans first
      const existingResponse = await fetch(`${BASE_URL}/subplans/`);
      const existingPlans = await existingResponse.json();

      // Check if a plan with same plan_name and user_type exists (case-insensitive)
      const isDuplicate = existingPlans.some(
        (plan) =>
          plan.plan_name.toLowerCase() === formData.plan_name.toLowerCase() &&
          plan.user_type.toLowerCase() === formData.user_type.toLowerCase() &&
          (!currentItem.plan || plan.plan_id !== currentItem.plan.plan_id), // Allow update to the same item
      );

      if (isDuplicate) {
        alert(
          `A plan with name "${formData.plan_name}" already exists for user type "${formData.user_type}".`,
        );
        return; // Stop submission
      }

      let razorPlanId = null;

      // Create Razorpay plan only if it's not a Free plan and not editing existing plan
      if (
        formData.plan_name.toLowerCase() !== "free" &&
        Number(formData.charges) > 0 &&
        !currentItem.plan
      ) {
        try {
          // Map plan_name to Razorpay period and interval
          let period, interval;

          switch (formData.plan_name) {
            case "1 Months":
              period = "monthly";
              interval = 1;
              break;
            case "3 Months":
              period = "monthly";
              interval = 3;
              break;
            case "6 Months":
              period = "monthly";
              interval = 6;
              break;
            case "12 Months":
              period = "yearly";
              interval = 1;
              break;
            case "Lifetime":
              period = "yearly";
              interval = 1; // Razorpay doesn't have lifetime, use yearly as base
              break;
            default:
              period = "monthly";
              interval = 1;
          }

          // Create Razorpay plan
          const razorpayResponse = await fetch(
            `${RAZORPAY_URL}/api/razorpay/plan`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                plan_name: formData.plan_name,
                amount: Math.round(Number(formData.charges) * 1.18),
                currency: "INR",
                period: period,
                interval: interval,
              }),
            },
          );

          const razorpayData = await razorpayResponse.json();

          if (razorpayData.success) {
            razorPlanId = razorpayData.plan.id;
          } else {
            console.error(
              "Failed to create Razorpay plan:",
              razorpayData.error,
            );
            // Don't stop the flow, continue without Razorpay plan ID
          }
        } catch (razorpayErr) {
          console.error("Error creating Razorpay plan:", razorpayErr);
          // Continue with the main plan creation even if Razorpay fails
        }
      }

      // Prepare final payload with Razorpay plan ID if available
      const finalPayload = {
        ...formData,
        razor_plan_id:
          razorPlanId ||
          (currentItem.plan ? currentItem.plan.razor_plan_id : null),
      };

      const url = currentItem.plan
        ? `${BASE_URL}/subplans/${currentItem.plan.plan_id}/`
        : `${BASE_URL}/subplans/`;
      const method = currentItem.plan ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setModalOpen((prev) => ({ ...prev, plans: false }));
      setRefreshFlag((prev) => ({ ...prev, plans: !prev.plans }));
    } catch (err) {
      console.error("Error submitting plan:", err);
      setError(err.message);
    }
  };
  const handleAddOnPlanSubmit = async (formData) => {
    console.log("Starting add-on plan submission...");
    console.log("Form data:", formData);
    console.log("Current item:", currentItem.addOnPlan);

    try {
      // Step 1: Fetch existing add-on plans
      const existingResponse = await fetch(`${BASE_URL}/add-on/`);
      if (!existingResponse.ok) {
        throw new Error(
          `Failed to fetch existing add-on plans. Status: ${existingResponse.status}`,
        );
      }
      const existingAddOns = await existingResponse.json();

      // Step 2: Check for duplicate based on both `charges` and `user_type`
      const isDuplicate = existingAddOns.some(
        (addOn) =>
          Number(addOn.charges) === Number(formData.charges) &&
          addOn.user_type === formData.user_type &&
          (!currentItem.addOnPlan ||
            addOn.addOn_id !== currentItem.addOnPlan.addOn_id), // exclude self on edit
      );

      if (isDuplicate) {
        alert(
          "An Add-on plan with the same charges and user type already exists!",
        );
        return;
      }

      const url = currentItem.addOnPlan
        ? `${BASE_URL}/add-on/${currentItem.addOnPlan.addOn_id}/`
        : `${BASE_URL}/add-on/`;
      const method = currentItem.addOnPlan ? "PUT" : "POST";

      console.log(`Making ${method} request to: ${url}`);

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        console.log("Response not OK, parsing error details...");
        const errorData = await response.json().catch(() => ({}));
        console.log("Error details:", errorData);

        let errorMessage = "Something went wrong";
        if (response.status === 400) errorMessage = "Invalid data provided";
        else if (response.status === 401) errorMessage = "Please login again";
        else if (response.status === 403) errorMessage = "Not authorized";
        else if (response.status === 404)
          errorMessage = "Add-on plan not found";
        else if (response.status === 500) errorMessage = "Server error";

        console.log("Throwing error:", errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("Success! Response data:", result);

      setModalOpen((prev) => ({ ...prev, addOnPlans: false }));
      setRefreshFlag((prev) => ({ ...prev, addOnPlans: !prev.addOnPlans }));

      console.log("State updated, operation completed successfully");
    } catch (err) {
      console.error("Error in handleAddOnPlanSubmit:", {
        error: err,
        message: err.message,
        stack: err.stack,
      });
      setError(err.message);
    }
  };

  const handleRewardSubmit = async (formData) => {
    try {
      const url = currentItem.reward
        ? `${BASE_URL}/rewards/${currentItem.reward.rew_id}/`
        : `${BASE_URL}/rewards/`;
      const method = currentItem.reward ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setModalOpen((prev) => ({ ...prev, rewards: false }));
      setRefreshFlag((prev) => ({ ...prev, rewards: !prev.rewards }));
    } catch (err) {
      console.error("Error submitting reward:", err);
      setError(err.message);
    }
  };

  if (loading.plans && loading.addOnPlans && loading.rewards) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 2 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <AdminDashboard />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Admin Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Subscription Plans" />
          <Tab label="Referral Rewards" />
          <Tab label="AddOn Plans" />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <>
          <SubscriptionTable
            plans={plans}
            onAddClick={handleAddPlanClick}
            onEditClick={handleEditPlanClick}
            onDeleteClick={handleDeletePlanClick}
            loading={loading.plans}
            setRefreshFlag={setRefreshFlag}
          />

          <SubscriptionForm
            open={modalOpen.plans}
            onClose={() => setModalOpen((prev) => ({ ...prev, plans: false }))}
            currentItem={currentItem.plan}
            onSubmit={handlePlanSubmit}
            userId={userId}
          />
        </>
      ) : activeTab === 1 ? (
        <>
          <ReferralRewardTable
            rewards={rewards}
            onAddClick={handleAddRewardClick}
            onEditClick={handleEditRewardClick}
            onDeleteClick={handleDeleteRewardClick}
            loading={loading.rewards}
          />

          <ReferralRewardForm
            open={modalOpen.rewards}
            onClose={() =>
              setModalOpen((prev) => ({ ...prev, rewards: false }))
            }
            currentItem={currentItem.reward}
            onSubmit={handleRewardSubmit}
          />
        </>
      ) : (
        <>
          <AddonTable
            addOnPlans={addOnPlans}
            onAddClick={handleAddAddOnPlanClick}
            onEditClick={handleEditAddOnPlanClick}
            onDeleteClick={handleDeleteAddOnPlanClick}
            loading={loading.addOnPlans}
            setRefreshFlag={setRefreshFlag}
          />

          <AddonForm
            open={modalOpen.addOnPlans}
            onClose={() =>
              setModalOpen((prev) => ({ ...prev, addOnPlans: false }))
            }
            currentItem={currentItem.addOnPlan}
            onSubmit={handleAddOnPlanSubmit}
            userId={userId}
          />
        </>
      )}
    </Container>
  );
};

export default AdminSubscription;
